import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { 
  Upload, 
  FileSpreadsheet, 
  MapPin, 
  ChevronLeft, 
  ChevronRight 
} from "lucide-react";

const PricelistZonePage = () => {
  const [zones, setZones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState(null);

  // --- 1. STATE PAGINATION ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // Sesuai request: 20 data per halaman

  const API_URL = "/api";

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      const response = await axios.get(`${API_URL}/zones`); 
      setZones(response.data);
    } catch (error) {
      console.error("Gagal ambil data", error);
      toast.error("Gagal mengambil data zona");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);
        
        const formattedData = data.map(row => ({
          country_name: row['Country'] || row['country'],
          country_code: row['Code'] || row['code'],
          zone_group: String(row['Zone'] || row['zone'])
        })).filter(item => item.country_code); 

        uploadDataToBackend(formattedData);
      } catch (err) {
        console.error("Error parsing excel:", err);
        toast.error("Gagal membaca file Excel");
      }
    };
    reader.readAsBinaryString(file);
  };

  const uploadDataToBackend = async (data) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/zones/import`, data);
      setStats(response.data.summary);
      toast.success("Import Berhasil!");
      fetchZones();
      setCurrentPage(1); // Reset ke halaman 1 setelah upload
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Gagal import ke database.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- 2. LOGIC SLICING DATA ---
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentZones = zones.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(zones.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="max-w-6xl mx-auto space-y-6 p-6">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Zone Management</h1>
          <p className="text-gray-500 mt-1">Upload data Excel untuk update Country & Zone</p>
        </div>
        
        {/* Tombol Upload Manual */}
        <div className="relative">
          <input 
            type="file" 
            accept=".xlsx, .xls" 
            onChange={handleFileUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
            disabled={isLoading}
          />
          <button className={`flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <FileSpreadsheet className="w-4 h-4" />
            {isLoading ? "Uploading..." : "Import Excel"}
          </button>
        </div>
      </div>

      {/* Info Stats Box */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 border border-green-200 p-4 rounded-xl text-center">
            <div className="text-2xl font-bold text-green-700">+{stats.added}</div>
            <div className="text-sm text-green-600 font-medium">Data Baru</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl text-center">
            <div className="text-2xl font-bold text-blue-700">{stats.updated}</div>
            <div className="text-sm text-blue-600 font-medium">Diupdate (Replace)</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl text-center">
            <div className="text-2xl font-bold text-gray-700">{stats.skipped}</div>
            <div className="text-sm text-gray-600 font-medium">Tidak Berubah</div>
          </div>
        </div>
      )}

      {/* Tabel Manual */}
      <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Data Zones ({zones.length} Countries)</h3>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Page {currentPage} of {totalPages || 1}</span>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-4 text-sm font-medium text-gray-500 w-16">No.</th>
                <th className="p-4 text-sm font-medium text-gray-500">Code</th>
                <th className="p-4 text-sm font-medium text-gray-500">Country Name</th>
                <th className="p-4 text-sm font-medium text-gray-500">Zone Group</th>
                <th className="p-4 text-sm font-medium text-gray-500 text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {zones.length === 0 ? (
                 <tr>
                   <td colSpan="5" className="p-8 text-center text-gray-400">
                     <div className="flex flex-col items-center justify-center gap-2">
                        <MapPin className="w-8 h-8 opacity-20" />
                        <span>Belum ada data. Silahkan Import Excel.</span>
                     </div>
                   </td>
                 </tr>
              ) : (
                // Render 'currentZones' bukan 'zones'
                currentZones.map((zone, idx) => (
                  <tr key={zone.id || idx} className="hover:bg-gray-50">
                    <td className="p-4 text-gray-400 text-sm">
                        {(currentPage - 1) * itemsPerPage + idx + 1}
                    </td>
                    <td className="p-4 font-mono text-blue-600 font-bold">{zone.country_code}</td>
                    <td className="p-4 font-medium text-gray-900">{zone.country_name}</td>
                    <td className="p-4">
                      <span className="inline-block bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-bold">
                        Zone {zone.zone_group}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-md border border-green-100">
                        Active
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* --- 3. FOOTER PAGINATION --- */}
        {zones.length > 0 && (
          <div className="bg-gray-50 px-6 py-4 border-t flex items-center justify-between">
            <div className="text-sm text-gray-500 hidden sm:block">
                Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, zones.length)}</strong> of <strong>{zones.length}</strong> entries
            </div>

            <div className="flex items-center gap-2 mx-auto sm:mx-0">
                <button 
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 border rounded-md bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 transition"
                >
                    <ChevronLeft size={16} />
                </button>
                
                <span className="text-sm font-medium text-gray-700 px-4">
                   {currentPage}
                </span>

                <button 
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 border rounded-md bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 transition"
                >
                    <ChevronRight size={16} />
                </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PricelistZonePage;